import { getApiServiceName, SERVICE_OPTIONS } from 'constants/serviceDistribution';
import { SeasonContentInterface } from 'content/season/types/seasonContentTypes';
import { SeasonData } from 'type/seasonType';
import { slugify } from 'utils/slugify';

export interface PosterCheckResult {
  orientation: {
    portrait: boolean;
    landscape: boolean;
  };
  languages: {
    ko: boolean;
    en: boolean;
    vi: boolean;
    'zh-hans': boolean;
  };
  isValid: boolean;
  message: string;
  duplicates?: Array<{
    key: string;
    language: string;
    orientation: string;
    count: number;
    posters: any[];
  }>;
}

export interface ContentsCheckResult {
  status: 'success' | 'warning' | 'error';
  message: string;
  count: number;
  statusCounts: Record<string, number>;
  contentTypeCounts: Record<string, number>;
  contents: SeasonContentInterface[];
  titleErrors?: Array<{
    id: number;
    title: string;
    expected: string;
  }>;
  slugErrors?: Array<{
    id: number;
    episode_order: number;
    episode_part: any;
    current: string;
    expected: string;
  }>;
}

export interface ServiceAutoDistributionResult {
  [serviceName: string]: {
    enabled: boolean;
    auto_publish?: boolean;
    subtitle_langs?: string[];
    service_name?: string;
  };
}

export interface DistributionStatusResult {
  [serviceName: string]: {
    enabled: boolean;
    id?: number;
    status?: string;
    auto_publish?: boolean;
    content_consumer_name?: string;
    created?: string;
    modified?: string;
  };
}

export interface DistributionReflectionResult {
  serviceStatusMessages: string[];
}

/**
 * Validates season posters for duplicates
 */
export const validatePosters = (season: SeasonData): PosterCheckResult => {
  const posters = season.posters || [];
  const requiredLanguages = ['ko', 'en', 'vi', 'zh-hans'];

  const orientationCheck = {
    portrait: posters.some(p => p.orientation === 'portrait'),
    landscape: posters.some(p => p.orientation === 'landscape'),
  };

  const languageCheck: {
    ko: boolean;
    en: boolean;
    vi: boolean;
    'zh-hans': boolean;
  } = {
    ko: false,
    en: false,
    vi: false,
    'zh-hans': false,
  };
  requiredLanguages.forEach(lang => {
    languageCheck[lang as keyof typeof languageCheck] = posters.some(p => p.language === lang);
  });

  // Check for duplicates by lang + orientation combination
  const duplicateMap = new Map<string, any[]>();
  posters.forEach(poster => {
    const key = `${poster.language}-${poster.orientation}`;
    if (!duplicateMap.has(key)) {
      duplicateMap.set(key, []);
    }
    duplicateMap.get(key)!.push(poster);
  });

  const duplicates: any[] = [];
  duplicateMap.forEach((posterList, key) => {
    if (posterList.length > 1) {
      duplicates.push({
        key,
        language: posterList[0].language,
        orientation: posterList[0].orientation,
        count: posterList.length,
        posters: posterList,
      });
    }
  });

  let isValid = true;
  let message = 'All poster data is normal.';
  if (duplicates.length > 0) {
    isValid = false;
    message = `Duplicate Posters: ${duplicates
      .map(d => `${d.language}-${d.orientation} (${d.count} items)`)
      .join(', ')}`;
  }

  return {
    orientation: orientationCheck,
    languages: languageCheck,
    isValid,
    message,
    duplicates: duplicates.length > 0 ? duplicates : undefined,
  };
};

/**
 * Validates season contents (slug, status, etc.)
 */
export const validateContents = (
  contentsList: SeasonContentInterface[],
  season: SeasonData
): ContentsCheckResult => {
  const count = contentsList.length;
  const statusCounts: Record<string, number> = {};
  const contentTypeCounts: Record<string, number> = {};
  const titleErrors: any[] = [];
  const slugErrors: any[] = [];

  contentsList.forEach(content => {
    statusCounts[content.status] = (statusCounts[content.status] || 0) + 1;
    contentTypeCounts[content.content_type] = (contentTypeCounts[content.content_type] || 0) + 1;

    if (content.episode_order !== null && content.episode_order !== undefined) {
      if (season.slug) {
        const episodePart = (content as any).episode_part;
        let slugBase = `${season.slug}: E${content.episode_order}`;
        if (episodePart !== null && episodePart !== undefined && episodePart !== '') {
          slugBase += `-${episodePart}`;
        }
        const expectedSlug1 = slugify(slugBase);
        const expectedSlug2 = episodePart
          ? `${season.slug}-e${content.episode_order}-${episodePart}`
          : `${season.slug}-e${content.episode_order}`;
        if (content.slug !== expectedSlug1 && content.slug !== expectedSlug2) {
          slugErrors.push({
            id: content.id,
            episode_order: content.episode_order,
            episode_part: episodePart,
            current: content.slug,
            expected: expectedSlug1,
          });
        }
      }
    }
  });

  let status: 'success' | 'warning' | 'error' = 'success';
  let message = `Total ${count} contents are connected.`;

  if (count === 0) {
    status = 'warning';
    message = 'No connected contents.';
  } else if (titleErrors.length > 0 || slugErrors.length > 0) {
    status = 'warning';
    const issues: string[] = [];
    if (slugErrors.length > 0) {
      issues.push(`Slug mismatch: ${slugErrors.length} items`);
    }
    message = issues.join(' / ');
  }

  return {
    status,
    message,
    count,
    statusCounts,
    contentTypeCounts,
    contents: contentsList,
    titleErrors: titleErrors.length > 0 ? titleErrors : undefined,
    slugErrors: slugErrors.length > 0 ? slugErrors : undefined,
  };
};

/**
 * Checks service auto distributions for a season
 * If service_auto_distribution_inherit is true, checks series distributions instead
 */
export const checkServiceAutoDistributions = async (
  season: SeasonData,
  seriesService: any
): Promise<ServiceAutoDistributionResult> => {
  let serviceAutoDistributions = season.service_auto_distributions || [];
  const result: ServiceAutoDistributionResult = {};

  if (season.service_auto_distribution_inherit) {
    const series = season.series;
    if (series) {
      const seriesData = await seriesService.fetchSeriesWithI18n(Number(series));
      serviceAutoDistributions = seriesData.service_auto_distributions || [];
    }
  }

  SERVICE_OPTIONS.forEach(service => {
    const currentEnv = process.env.REACT_APP_ENV === 'production' ? 'production' : 'staging';
    const apiServiceName = getApiServiceName(service.name, currentEnv);
    const serviceAutoDist = serviceAutoDistributions.find(d => d.service_name === apiServiceName);

    result[`${service.name}`] = serviceAutoDist
      ? {
          enabled: true,
          auto_publish: serviceAutoDist.auto_publish,
          subtitle_langs:
            serviceAutoDist.subtitle_langs ||
            serviceAutoDist.subtitles?.map((s: any) => s.subtitle_lang) ||
            [],
          service_name: serviceAutoDist.service_name,
        }
      : { enabled: false };
  });

  return result;
};

/**
 * Checks distribution status for a season
 */
export const checkDistributionStatus = (season: SeasonData): DistributionStatusResult => {
  const distributions = season.distributions || [];
  const result: DistributionStatusResult = {};

  SERVICE_OPTIONS.forEach(service => {
    const dist = distributions.find(d => d.content_consumer_name === service.apiName);
    result[`${service.name}`] = dist
      ? {
          enabled: true,
          id: dist.id,
          status: dist.status,
          auto_publish: dist.auto_publish,
          content_consumer_name: dist.content_consumer_name,
          created: dist.created,
          modified: dist.modified,
        }
      : { enabled: false };
  });

  return result;
};

/**
 * Checks if newly modified content has been reflected in distributions
 * Excludes Amasian from the check
 */
export const checkDistributionReflection = (
  distributionStatus: DistributionStatusResult,
  seasonData: SeasonData
): DistributionReflectionResult => {
  const serviceStatusMessages: string[] = [];

  SERVICE_OPTIONS.forEach(service => {
    // Amasian is excluded from Distribution status check
    if (service.apiName === 'amasian') {
      return;
    }
    const distStatus = distributionStatus[service.name];
    if (distStatus?.enabled) {
      // If status is not COMPLETED, content is not reflected
      if (distStatus.status !== 'COMPLETED') {
        serviceStatusMessages.push(
          `${service.name}: Newly modified content has not been reflected`
        );
      } else if (
        distStatus.modified &&
        seasonData.modified &&
        new Date(distStatus.modified) < new Date(seasonData.modified)
      ) {
        // COMPLETED but modified date is earlier, content is not reflected
        serviceStatusMessages.push(
          `${service.name}: Newly modified content has not been reflected`
        );
      } else if (
        distStatus.modified &&
        seasonData.modified &&
        new Date(distStatus.modified) >= new Date(seasonData.modified)
      ) {
        // COMPLETED and modified date is same or later, content is reflected
        serviceStatusMessages.push(`${service.name}: Newly modified content has been reflected`);
      }
    }
  });

  return {
    serviceStatusMessages,
  };
};
